import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Popover, Heading, Spacer, FormField, Flex, Label, Popper, TextButton, FormHelp, AdornedInput, Adornment, Input } from '@zillow/constellation';
import { useFormContext, useFormState } from 'react-hook-form';
import InputMask from 'inputmask';
import { event } from '@zillow/universal-analytics';
import { handleFieldClickEvent } from '../VALoanCalculator/VALoanCalculator.Clickstream.js';

/**
 *
 */
function TextField(_ref) {
  var _proxyFormState$error;
  var label = _ref.label,
    leftAdornment = _ref.leftAdornment,
    rightAdornment = _ref.rightAdornment,
    name = _ref.name,
    config = _ref.config,
    description = _ref.description,
    textButtonName = _ref.textButtonName,
    _onChange = _ref.onChange,
    _onBlur = _ref.onBlur,
    autoComplete = _ref.autoComplete,
    format = _ref.format,
    required = _ref.required,
    popover = _ref.popover,
    placeholder = _ref.placeholder,
    disabled = _ref.disabled,
    textButtonHref = _ref.textButtonHref,
    mask = _ref.mask;
  var form = useFormContext();
  var proxyFormState = useFormState();
  var error = proxyFormState === null || proxyFormState === void 0 || (_proxyFormState$error = proxyFormState.errors[name]) === null || _proxyFormState$error === void 0 ? void 0 : _proxyFormState$error.message;
  var field = form.register(name, config);
  var inputRef = useRef(null);

  // Format on mount
  useEffect(function () {
    if (format) {
      var defaultValue = form.getValues(name);
      if (defaultValue) {
        inputRef.current.value = format(defaultValue);
      }
    }
  }, []);
  var trigger = popover ? /*#__PURE__*/React.createElement(Popover, {
    header: /*#__PURE__*/React.createElement(Heading, {
      level: 6
    }, popover === null || popover === void 0 ? void 0 : popover.title),
    body: popover === null || popover === void 0 ? void 0 : popover.body
  }) : false;
  var onOpenInteractionEvent = popover !== null && popover !== void 0 && popover.interactionEventCmsInfo ? {
    envelope: {
      event_type_id: '5030',
      event_type_version_id: '1',
      event_template_id: '297',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_tooltip',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_open'
    },
    cms_info: popover.interactionEventCmsInfo
  } : null;
  var onCloseInteractionEvent = popover !== null && popover !== void 0 && popover.interactionEventCmsInfo ? {
    envelope: {
      event_type_id: '2051',
      event_type_version_id: '1',
      event_template_id: '126',
      event_template_version_id: '1'
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'cms',
      trigger_source_nm: 'cms_tooltip',
      trigger_object_nm: 'no_trigger_object'
    },
    semantic: {
      semantic_event_nm: 'cms_close'
    },
    cms_info: popover.interactionEventCmsInfo
  } : null;
  return /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(FormField, {
    disabled: disabled,
    label: /*#__PURE__*/React.createElement(Flex, {
      display: "flex",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Label, null, label), trigger && /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", /*#__PURE__*/React.createElement(Popper, {
      triggered: trigger,
      onOpen: function onOpen() {
        return event(onOpenInteractionEvent);
      },
      onClose: function onClose() {
        return event(onCloseInteractionEvent);
      }
    })), textButtonName ? /*#__PURE__*/React.createElement(Flex, {
      display: "flex",
      justifyContent: "space-between",
      marginLeft: "auto"
    }, /*#__PURE__*/React.createElement(TextButton, {
      as: "a",
      target: "_blank",
      rel: "nofollow",
      href: textButtonHref,
      onClick: function onClick() {
        return handleFieldClickEvent({
          textButtonName: textButtonName
        }, 'button');
      }
    }, textButtonName)) : ''),
    required: required,
    description: /*#__PURE__*/React.createElement(FormHelp, {
      error: Boolean(error)
    }, error || description || /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0")),
    control: /*#__PURE__*/React.createElement(AdornedInput, {
      error: Boolean(error),
      leftAdornment: leftAdornment && /*#__PURE__*/React.createElement(Adornment, null, leftAdornment),
      rightAdornment: rightAdornment && /*#__PURE__*/React.createElement(Adornment, null, rightAdornment),
      input: /*#__PURE__*/React.createElement(Input, _extends({}, field, {
        placeholder: placeholder,
        onBlur: function onBlur(e) {
          field.onBlur(e);
          _onBlur === null || _onBlur === void 0 || _onBlur(e);
        },
        onChange: function onChange(e) {
          field.onChange(e);
          _onChange === null || _onChange === void 0 || _onChange(e);
        },
        onInput: format ? function (e) {
          e.target.value = format(e.target.value);
        } : undefined
        // The "mask" properties will not update, as this is memoized.
        ,
        ref: useCallback(function (el) {
          if (el && mask) {
            var masker = new InputMask(mask);
            masker.mask(el);
          }
          inputRef.current = el;
          field.ref(el);
        }, []),
        autoComplete: autoComplete
      }))
    })
  }));
}
TextField.propTypes = process.env.NODE_ENV !== "production" ? {
  leftAdornment: PropTypes.string,
  rightAdornment: PropTypes.string,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  description: PropTypes.node,
  name: PropTypes.string.isRequired,
  config: PropTypes.shape({
    required: PropTypes.string,
    validate: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func])
  }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  autoComplete: PropTypes.string,
  format: PropTypes.func,
  popover: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.element,
    interactionEventCmsInfo: PropTypes.object
  }),
  mask: PropTypes.shape({}),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  textButtonName: PropTypes.string,
  textButtonHref: PropTypes.string
} : {};
TextField.defaultProps = {
  leftAdornment: undefined,
  rightAdornment: undefined,
  description: undefined,
  config: undefined,
  format: undefined,
  mask: undefined,
  placeholder: undefined
};

export { TextField as default };
